import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNodeConfigBase } from './node-config-base';
import {
  CONDITION_SOURCE_OPTIONS,
  CRM_OBJECT_TYPE_OPTIONS,
  DEFAULT_WORKFLOW_TIMEZONE,
  LIFECYCLE_FIELD_OPTIONS,
  PROPERTY_CHANGED_SUPPORTED_KEYS,
  SEGMENT_EVENT_OPTIONS,
  WORKFLOW_TIMEZONE_OPTIONS
} from './config-options';
import { WorkflowCrmEventOptionResponse } from '../../../../models/workflow-builder.model';
import { EntitySelectComponent, EntitySelectPage } from '../../../../../../../shared/components/entity-select/entity-select.component';
import { SegmentService } from '../../../../../../segments/services/segment-api.service';
import { Segment } from '../../../../../../segments/models/segment.model';
import { WorkflowEmailTemplateApiService, EmailTemplateResponse } from '../../../../services/workflow-email-template-api.service';
import { WorkflowWebhookActionApiService } from '../../../../services/workflow-webhook-action-api.service';
import { WorkflowWebhookEndpointResponse } from '../../../../models/workflow-webhook-action.model';
import { WorkflowPropertySelectComponent } from './property-select.component';
import { buildDatePropertyOptions, buildPropertyOptions, WorkflowValueCatalogKey } from '../../../models/workflow-variable-catalog';
import { WorkflowCatalogValue, WorkflowValueCatalogService } from '../../../services/workflow-value-catalog.service';
import { AuthService } from '../../../../../../auth-pages/services/auth.service';
import { ContactCycleApiService } from '../../../../../../contact-cycles/services/contact-cycle-api.service';
import { ContactStateApiService } from '../../../../../../contact-states/services/contact-state-api.service';
import { MarketingFormsService } from '../../../../../../marketing-forms/service/marketing-forms.service';
import { MarketingCampaignApiService } from '../../../../../../marketing-email/services/marketing-campaign-api.service';
import { MarketingCampaign } from '../../../../../../marketing-email/models/marketing-campaign.model';
import { FormListModel } from '../../../../../../marketing-forms/models/form.model';

import {
  WorkflowConditionValueComponent,
  WorkflowConditionValueParts,
  readConditionValueParts
} from './condition-value.component';

interface FilterRow extends WorkflowConditionValueParts { source: string; property: string; operator: string; }

/** Comparación vacía de una fila recién añadida (mismas claves que lee el backend). */
const EMPTY_VALUE_PARTS: WorkflowConditionValueParts = { value: '', from: null, to: null, values: null };

/** Opción de los desplegables Desde/Hasta de LIFECYCLE_CHANGED (ciclo o etapa del contacto). */
interface LifecycleOption { id: number; name: string; }

/** Subtipos de disparador cuyo ÚNICO campo es `config.event` (misma UI para los cuatro). */
const EVENT_TRIGGER_SUBTYPES = ['CRM_EVENT', 'QUOTE_EVENT', 'MEETING_EVENT', 'CHATFLOW_EVENT'];

/** Un `<optgroup>` del desplegable de eventos (Contactos, Empresas, Negocios…). */
interface CrmEventGroup { label: string; options: WorkflowCrmEventOptionResponse[]; }

/**
 * Evento guardado que NO aparece entre las opciones ofrecidas. Se conserva como opción extra en
 * vez de perderse en silencio: si el nodo tenía `SUBSCRIPTION_CREATED` (evento retirado del
 * catálogo), el usuario debe VER qué tenía para poder cambiarlo con conocimiento de causa.
 */
interface OrphanEventOption { key: string; label: string; groupLabel: string; note: string; warning: string; }

/**
 * TRIGGER — disparador del workflow. Subtipos: MANUAL_ENROLLMENT, CRM_EVENT, QUOTE_EVENT,
 * MEETING_EVENT, CHATFLOW_EVENT, PROPERTY_FILTER, PROPERTY_CHANGED, SEGMENT_MEMBERSHIP,
 * DATE_BASED, EMAIL_INTERACTION, INBOUND_WEBHOOK, LIFECYCLE_CHANGED.
 *
 * Los CUATRO disparadores de evento (CRM_EVENT, QUOTE_EVENT, MEETING_EVENT, CHATFLOW_EVENT)
 * comparten formulario porque comparten contrato: un único `config.event`. Sus opciones salen
 * del catálogo del backend (`crmEvents` + `items[].eventOptions`), NUNCA de una lista local:
 * se muestran etiquetas en español agrupadas por objeto, la descripción del evento elegido tal
 * y como la escribe el backend, y los eventos que no aplican al `objectType` del workflow
 * deshabilitados con su motivo (en vez de ocultos, para que el usuario no los busque en vano).
 */
@Component({
  selector: 'app-wf-trigger-config',
  standalone: true,
  imports: [
    CommonModule,
    SelectValueSyncDirective,
    EntitySelectComponent,
    WorkflowPropertySelectComponent,
    WorkflowConditionValueComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isEventTrigger()) {
      <!-- CRM_EVENT / QUOTE_EVENT / MEETING_EVENT / CHATFLOW_EVENT: los cuatro tienen el mismo
           y único campo (config.event), así que comparten formulario. -->
      <div class="space-y-3">
        <div>
          <label [class]="labelClass">Evento que inicia el workflow</label>
          <select [class]="fieldClass" [value]="str('event')" [disabled]="readOnly()"
                  (change)="onEventChange(inputValue($event))">
            <option value="">Elige un evento…</option>
            @for (group of eventGroups(); track group.label) {
              <optgroup [label]="group.label">
                @for (opt of group.options; track opt.key) {
                  <option [value]="opt.key" [disabled]="!opt.applicable">{{ opt.label }}{{ opt.applicable ? '' : ' — no aplica a este workflow' }}</option>
                }
              </optgroup>
            }
            @if (orphanEvent(); as orphan) {
              <optgroup [label]="orphan.groupLabel">
                <option [value]="orphan.key">{{ orphan.label }}{{ orphan.note }}</option>
              </optgroup>
            }
          </select>
        </div>

        @if (selectedEvent(); as evt) {
          @if (evt.description) {
            <p class="rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-400">
              {{ evt.description }}
            </p>
          }
          @if (!evt.applicable) {
            <p class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
              <i class="fa-solid fa-triangle-exclamation mr-1"></i>
              {{ evt.notApplicableReason || 'Este evento no aplica al tipo de objeto del workflow: elegirlo produce un workflow que no se ejecutaría nunca.' }}
            </p>
          }
        }

        <!--
          INC_CRM_020 (9) inc51 — «Etapa del contacto modificada» (y el resto de eventos de
          cambio) solo podía decir "cuando cambie", nunca "de Nuevo a Calificado". Los eventos de
          transición traen en su payload el valor de origen y el de destino, así que aquí se
          ofrecen los dos desplegables con las etapas REALES del CRM y «Cualquiera» como opción,
          que es lo que el backend interpreta cuando el filtro va vacío.
        -->
        @if (transitionCatalog(); as catalog) {
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Desde</label>
              <select [class]="fieldClass" [value]="str('fromId')" [disabled]="readOnly()"
                      (change)="patchConfig({ fromId: numberOrNull(inputValue($event)) })">
                <option value="">{{ anyTransitionLabel() }}</option>
                @for (o of transitionOptions(); track o.id) { <option [value]="o.id">{{ o.label }}</option> }
                @if (transitionMissing('fromId')) {
                  <option [value]="str('fromId')">#{{ str('fromId') }} (ya no está en el catálogo)</option>
                }
              </select>
            </div>
            <div>
              <label [class]="labelClass">Hacia</label>
              <select [class]="fieldClass" [value]="str('toId')" [disabled]="readOnly()"
                      (change)="patchConfig({ toId: numberOrNull(inputValue($event)) })">
                <option value="">{{ anyTransitionLabel() }}</option>
                @for (o of transitionOptions(); track o.id) { <option [value]="o.id">{{ o.label }}</option> }
                @if (transitionMissing('toId')) {
                  <option [value]="str('toId')">#{{ str('toId') }} (ya no está en el catálogo)</option>
                }
              </select>
            </div>
          </div>
          <p class="rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-400">
            El workflow se dispara SOLO cuando el cambio cumple la transición configurada. Deja los
            dos en «{{ anyTransitionLabel() }}» para reaccionar a cualquier cambio.
          </p>
        }

        @if (orphanEvent(); as orphan) {
          @if (orphan.warning) {
            <p class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
              <i class="fa-solid fa-triangle-exclamation mr-1"></i>{{ orphan.warning }}
            </p>
          }
        }

        @if (!eventCatalogLoaded()) {
          <p class="text-[11px] text-error-500">
            No se pudo cargar el catálogo de eventos del CRM. Vuelve a abrir el editor para reintentarlo; mientras tanto
            puedes ajustar el evento en el modo avanzado (JSON).
          </p>
        }
      </div>
    } @else {
    @switch (subtype()) {
      @case ('MANUAL_ENROLLMENT') {
        <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
          Los registros se inscriben manualmente. Este disparador no tiene criterios que configurar.
        </p>
      }

      @case ('LIFECYCLE_CHANGED') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Campo que cambia</label>
            <select [class]="fieldClass" [value]="lifecycleField()" [disabled]="readOnly()"
                    (change)="onLifecycleFieldChange(inputValue($event))">
              @for (f of lifecycleFields; track f.value) { <option [value]="f.value">{{ f.label }}</option> }
            </select>
            <p [class]="hintClass">
              El workflow se dispara cuando el contacto cambia de {{ lifecycleField() === 'STATE' ? 'etapa' : 'ciclo de vida' }}.
            </p>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Desde (opcional)</label>
              <select [class]="fieldClass" [value]="str('fromId')" [disabled]="readOnly()"
                      (change)="patchConfig({ fromId: numberOrNull(inputValue($event)) })">
                <option value="">(cualquiera)</option>
                @for (o of lifecycleOptions(); track o.id) { <option [value]="o.id">{{ o.name }}</option> }
                @if (lifecycleMissing('fromId')) {
                  <option [value]="str('fromId')">#{{ str('fromId') }} (ya no está en el catálogo)</option>
                }
              </select>
            </div>
            <div>
              <label [class]="labelClass">Hasta (opcional)</label>
              <select [class]="fieldClass" [value]="str('toId')" [disabled]="readOnly()"
                      (change)="patchConfig({ toId: numberOrNull(inputValue($event)) })">
                <option value="">(cualquiera)</option>
                @for (o of lifecycleOptions(); track o.id) { <option [value]="o.id">{{ o.name }}</option> }
                @if (lifecycleMissing('toId')) {
                  <option [value]="str('toId')">#{{ str('toId') }} (ya no está en el catálogo)</option>
                }
              </select>
            </div>
          </div>
          <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
            Deja <strong>Desde</strong> en «(cualquiera)» para disparar venga del valor que venga; normalmente basta con
            acotar <strong>Hasta</strong>, que es el valor al que acaba de llegar el contacto. Si dejas ambos en
            «(cualquiera)», el workflow se dispara con cualquier cambio de este campo.
          </p>
          @if (lifecycleLoadFailed()) {
            <p class="text-[11px] text-error-500">
              No se pudo cargar el catálogo. Puedes reintentar reabriendo el paso o fijar los ids en el modo avanzado (JSON).
            </p>
          }
        </div>
      }

      @case ('PROPERTY_FILTER') {
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Condiciones</label>
            <select class="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                    [value]="str('logic', 'AND')" [disabled]="readOnly()" (change)="patchConfig({ logic: inputValue($event) })">
              <option value="AND">Todas (Y)</option>
              <option value="OR">Alguna (O)</option>
            </select>
          </div>
          @for (row of filterRows(); track $index) {
            <div class="space-y-2 rounded-lg border border-gray-200 p-2.5 dark:border-gray-800">
              <div class="flex items-center gap-2">
                <select class="w-28 rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                        [value]="row.source" [disabled]="readOnly()" (change)="updateFilter($index, { source: inputValue($event) })">
                  @for (s of sourceOptions; track s.value) { <option [value]="s.value">{{ s.label }}</option> }
                </select>
                <div class="flex-1 min-w-0">
                  <app-wf-property-select
                    [value]="row.property" [options]="propertyOptions()" [disabled]="readOnly()"
                    placeholder="Elige una propiedad…"
                    (valueChange)="updateFilter($index, { property: $event })"></app-wf-property-select>
                </div>
                @if (filterRows().length > 1) {
                  <button type="button" class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:text-error-500 disabled:opacity-40"
                          [disabled]="readOnly()" (click)="removeFilter($index)"><i class="fa-solid fa-xmark text-xs"></i></button>
                }
              </div>
              <select class="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                      [value]="row.operator" [disabled]="readOnly()" (change)="updateFilter($index, { operator: inputValue($event) })">
                @for (o of operatorOptionsFor(propertyOptions(), row.property, row.operator); track o.value) {
                  <option [value]="o.value">{{ o.label }}</option>
                }
              </select>
              <app-wf-condition-value
                [operator]="row.operator" [value]="row.value" [from]="row.from" [to]="row.to" [values]="row.values"
                [property]="row.property" [options]="propertyOptions()"
                [disabled]="readOnly()" (patch)="updateFilter($index, $event)" />
            </div>
          }
          <button type="button" class="text-xs font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                  [disabled]="readOnly()" (click)="addFilter()">+ Añadir condición</button>
        </div>
      }

      @case ('PROPERTY_CHANGED') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Propiedad</label>
            <app-wf-property-select
              [value]="str('property')" [options]="propertyOptions()" [disabled]="readOnly()"
              placeholder="Elige la propiedad que cambia…"
              (valueChange)="patchConfig({ property: $event })"></app-wf-property-select>
            <p [class]="hintClass">El workflow se dispara cuando esta propiedad del registro cambia de valor.</p>
          </div>
          <!--
            INC_CRM_020 (9) inc49/50/53 — «Desde» y «Hasta» eran dos inputs de texto pasara lo que
            pasara con la propiedad elegida: para acotar por Estado había que saberse el valor
            exacto, y en una propiedad numérica o de empresa se seguía pidiendo texto. Ahora usan
            el MISMO editor que las condiciones, que se adapta a la propiedad (desplegable del
            catálogo, buscador de registro, número o fecha) y cae a texto libre si no la conoce.
          -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Desde (opcional)</label>
              <app-wf-condition-value
                [operator]="'EQUALS'" [value]="config()['from']" [property]="str('property')"
                [options]="propertyOptions()" [disabled]="readOnly()" placeholder="(cualquier valor)"
                (patch)="patchConfig({ from: $event['value'] })" />
            </div>
            <div>
              <label [class]="labelClass">Hasta (opcional)</label>
              <app-wf-condition-value
                [operator]="'EQUALS'" [value]="config()['to']" [property]="str('property')"
                [options]="propertyOptions()" [disabled]="readOnly()" placeholder="(cualquier valor)"
                (patch)="patchConfig({ to: $event['value'] })" />
            </div>
          </div>
          <p class="rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-400">
            Deja los dos vacíos para disparar con CUALQUIER cambio de la propiedad. Rellena solo
            <strong>Hasta</strong> para exigir el valor al que llega, o los dos para exigir la
            transición completa.
          </p>
          <!--
            Este disparador NO escucha cualquier campo: solo salta con los cambios que el CRM
            publica como transición (con valor anterior y nuevo). Elegir otra propiedad deja un
            flujo que no arranca nunca y sin ningún aviso, así que se dice cuáles sirven. Para el
            resto de campos, el disparador correcto es «Filtro de propiedades» sobre el evento de
            actualización.
          -->
          @if (propertyChangedSupported().length) {
            <p class="text-[11px] text-gray-500 dark:text-gray-400">
              Hoy solo se publican como cambio: <strong>{{ propertyChangedSupported().join(', ') }}</strong>.
              Con otra propiedad el flujo no llegará a arrancar; usa «Filtro de propiedades» para esos casos.
            </p>
          } @else {
            <p class="text-[11px] text-amber-600 dark:text-amber-400">
              Para este tipo de objeto todavía no se publica ningún cambio de propiedad: usa
              «Filtro de propiedades» sobre el evento de actualización.
            </p>
          }
        </div>
      }

      @case ('SEGMENT_MEMBERSHIP') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Segmento</label>
            <app-entity-select
              [value]="num('segmentId') || null" [displayText]="segmentName()"
              [searchFn]="segmentSearch" [labelFn]="segmentLabel" [disabled]="readOnly()"
              placeholder="Buscar segmento por nombre…"
              (valueChange)="patchConfig({ segmentId: $event })" (selected)="onSegmentSelected($event)"></app-entity-select>
          </div>
          <div>
            <label [class]="labelClass">Evento</label>
            <select [class]="fieldClass" [value]="str('event', 'ENTERED')" [disabled]="readOnly()"
                    (change)="patchConfig({ event: inputValue($event) })">
              @for (e of segmentEvents; track e.value) { <option [value]="e.value">{{ e.label }}</option> }
            </select>
          </div>
          <!--
            Ningún módulo del backend publica SEGMENT_ENTERED/SEGMENT_EXITED (comprobado: no existe
            ni un emisor). El disparador evalúa bien, pero nunca le llega nada, así que el workflow
            no arranca jamás. El catálogo lo dice en su descripción; aquí, que es donde el usuario
            lo configura, no se decía.
          -->
          <p class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
            <i class="fa-solid fa-triangle-exclamation mr-1"></i>
            Hoy el CRM <strong>no publica</strong> las entradas y salidas de segmento por su cuenta:
            este disparador solo se activa si un sistema externo envía el evento a
            <code>POST /api/workflow-events</code>. Para reaccionar a cambios del propio CRM usa
            «Filtro de propiedades» o «Evento del CRM».
          </p>
        </div>
      }

      <!--
        El selector ofrecía TODAS las propiedades del objeto: en un workflow de Negocio se elegía
        entre 20 de las que solo 3 son fechas. Elegir «Monto» o «Etapa» se guardaba sin un aviso y
        el flujo no se programaba nunca, porque en ejecución el valor no se puede leer como fecha.
        Ahora solo se listan las de tipo fecha (kind: DATE del catálogo de propiedades).
      -->
      @case ('DATE_BASED') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Propiedad de fecha</label>
            <app-wf-property-select
              [value]="str('dateProperty')" [options]="datePropertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad de fecha…"
              (valueChange)="patchConfig({ dateProperty: $event })"></app-wf-property-select>
            @if (!datePropertyOptions().length) {
              <p class="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
                <i class="fa-solid fa-triangle-exclamation mr-1"></i>
                Este tipo de objeto no expone ninguna fecha del registro. Escribir una propiedad a
                mano solo sirve si un sistema externo envía esa fecha en el cuerpo del evento a
                <code>POST /api/workflow-events</code>.
              </p>
            } @else {
              <p [class]="hintClass">
                La fecha se lee del registro (no hace falta que el evento la traiga) y el flujo se
                programa para ella más el desfase, a la hora indicada.
              </p>
            }
            <!--
              El nombre libre del selector invita a escribir un atributo personalizado, y no
              funciona: el motor de workflows no lee la tabla EAV en ningún punto y el modelo de
              atributos ni siquiera tiene tipo fecha. Decirlo aquí evita construir un disparador
              que no arranca nunca.
            -->
            <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Los <strong>atributos personalizados no se pueden usar</strong> como propiedad de
              fecha: los workflows no los leen.
            </p>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Desfase (días)</label>
              <input type="number" [class]="fieldClass" [value]="num('offsetDays', 0)" [disabled]="readOnly()"
                     (change)="patchConfig({ offsetDays: numberValue($event) })" />
              <p [class]="hintClass">Negativo = antes de la fecha (-3 = tres días antes).</p>
            </div>
            <div>
              <label [class]="labelClass">Hora</label>
              <input type="time" [class]="fieldClass" [value]="str('time', '09:00')" [disabled]="readOnly()"
                     (change)="patchConfig({ time: inputValue($event) })" />
            </div>
          </div>

          <!--
            La hora se interpretaba en UTC fija y sin decirlo, así que un «09:00» se ejecutaba a las
            04:00 de Lima. El nodo «Esperar hasta fecha», con los mismos campos, sí pedía la zona.
            El valor por defecto coincide con el del backend: no elegir nada da America/Lima.
          -->
          <div>
            <label [class]="labelClass">Zona horaria de esa hora</label>
            <select [class]="fieldClass" [value]="str('timezone', defaultTimezone)" [disabled]="readOnly()"
                    (change)="patchConfig({ timezone: inputValue($event) })">
              @for (tz of timezones; track tz) { <option [value]="tz">{{ tz }}</option> }
            </select>
          </div>

          <p class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
            <i class="fa-solid fa-triangle-exclamation mr-1"></i>
            La programación se calcula cuando ocurre algo con el registro (se crea, se actualiza…).
            <strong>No hay un repaso diario</strong>: un registro que nadie toca nunca queda programado,
            y si la fecha calculada ya pasó el disparo se descarta en vez de ejecutarse al instante.
          </p>
        </div>
      }

      <!--
        INC_CRM_020 (6) inc18 — «no existe aún el Disparador por envío de Formulario».
        El formulario se elige por nombre con el buscador; dejarlo vacío significa «cualquier
        formulario», que es la config por defecto del catálogo.
      -->
      @case ('FORM_SUBMISSION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Formulario</label>
            <app-entity-select
              [value]="num('formId') || null" [displayText]="formName()"
              [searchFn]="formSearch" [labelFn]="formLabel" [disabled]="readOnly()"
              placeholder="Cualquier formulario"
              (valueChange)="patchConfig({ formId: $event })" (selected)="onFormSelected($event)"></app-entity-select>
            <p [class]="hintClass">
              Déjalo vacío para que el workflow arranque con CUALQUIER formulario, o elige uno para
              acotarlo a ese.
            </p>
          </div>
          <p class="rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-400">
            Se inscribe el contacto que identifica el envío (se crea si no existía). Los envíos
            incompletos y los pendientes de confirmar por correo (doble opt-in) no disparan el flujo.
          </p>
        </div>
      }

      <!--
        INC_CRM_020 (6) inc19 — «no existe aún el Disparador por interacción con email de marketing».
        El panel anterior pedía una PLANTILLA de correo transaccional, que no es lo que se envía en
        una campaña, y listaba las interacciones con su nombre técnico en inglés. Ahora las
        etiquetas salen del catálogo del backend (fuente única) y el alcance se acota al email de
        marketing, que en este CRM es la propia campaña.
      -->
      @case ('EMAIL_INTERACTION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Interacción</label>
            <select [class]="fieldClass" [value]="str('event')" [disabled]="readOnly()"
                    (change)="patchConfig({ event: inputValue($event) })">
              <option value="">Cualquier interacción</option>
              @for (opt of emailInteractionOptions(); track opt.key) {
                <option [value]="opt.key">{{ opt.label }}</option>
              }
            </select>
            @if (selectedEmailInteraction(); as evt) {
              @if (evt.description) {
                <p class="mt-2 rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-400">
                  {{ evt.description }}
                </p>
              }
            }
            @if (emailInteractionOptions().length === 0) {
              <p class="mt-1 text-[11px] text-error-500">
                No se pudo cargar el catálogo de interacciones de email. Vuelve a abrir el editor para
                reintentarlo.
              </p>
            }
          </div>
          <div>
            <label [class]="labelClass">Alcance</label>
            <select [class]="fieldClass" [value]="str('scope', 'ANY')" [disabled]="readOnly()"
                    (change)="onEmailScopeChange(inputValue($event))">
              <option value="ANY">Cualquier email de marketing</option>
              <option value="CAMPAIGN">Un email de marketing concreto</option>
            </select>
          </div>
          @if (str('scope', 'ANY') === 'CAMPAIGN') {
            <div>
              <label [class]="labelClass">Email de marketing</label>
              <app-entity-select
                [value]="num('campaignId') || null" [displayText]="campaignName()"
                [searchFn]="campaignSearch" [labelFn]="campaignLabel" [sublabelFn]="campaignSublabel"
                [disabled]="readOnly()"
                placeholder="Buscar email de marketing por nombre…"
                (valueChange)="patchConfig({ campaignId: $event })" (selected)="onCampaignSelected($event)"></app-entity-select>
              <p [class]="hintClass">
                En este CRM cada email de marketing es una campaña: elegirla aquí acota el disparador
                a las interacciones con ese envío concreto.
              </p>
            </div>
          }
        </div>
      }

      @case ('INBOUND_WEBHOOK') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Clave del endpoint</label>
            @if (endpoints().length > 0) {
              <select [class]="fieldClass" [value]="str('endpointKey')" [disabled]="readOnly()"
                      (change)="patchConfig({ endpointKey: inputValue($event) })">
                <option value="">Elige un endpoint…</option>
                @for (ep of endpoints(); track ep.id) {
                  <option [value]="ep.endpointKey">{{ ep.name || ep.endpointKey }} ({{ ep.endpointKey }})</option>
                }
                @if (endpointKeyMissing()) {
                  <option [value]="str('endpointKey')">{{ str('endpointKey') }}</option>
                }
              </select>
              <p [class]="hintClass">Endpoints inbound existentes de este negocio. Gestiónalos en Endpoints Webhook.</p>
            } @else {
              <input type="text" [class]="fieldClass" [value]="str('endpointKey')" [disabled]="readOnly()"
                     (input)="patchConfig({ endpointKey: inputValue($event) })" />
              <p [class]="hintClass">No hay endpoints inbound registrados. Escribe la clave o créalos en Endpoints Webhook.</p>
            }
          </div>
          <div>
            <label [class]="labelClass">Objeto a emparejar</label>
            <select [class]="fieldClass" [value]="str('matchObjectType', 'CONTACT')" [disabled]="readOnly()"
                    (change)="patchConfig({ matchObjectType: inputValue($event) })">
              @for (o of crmObjectTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
            </select>
          </div>
          <!--
            INC_CRM_020 (9) inc54 — la ruta se escribía a ciegas: nadie conoce de memoria la forma
            del JSON que manda un sistema externo. El CRM guarda el ÚLTIMO cuerpo recibido por el
            endpoint y aquí se ofrecen sus rutas; mientras no haya llegado ninguna llamada se
            sugieren las habituales y siempre queda la opción de escribirla a mano.
          -->
          <div>
            <label [class]="labelClass">Ruta del id en el payload</label>
            @if (!customIdPath()) {
              <select [class]="fieldClass" [value]="str('matchObjectIdPath', 'id')" [disabled]="readOnly()"
                      (change)="onIdPathSelect(inputValue($event))">
                @for (path of idPathOptions(); track path) { <option [value]="path">{{ path }}</option> }
                @if (idPathMissing()) { <option [value]="str('matchObjectIdPath')">{{ str('matchObjectIdPath') }}</option> }
                <option value="__custom__">Otra ruta…</option>
              </select>
            } @else {
              <div class="flex items-center gap-2">
                <input type="text" [class]="fieldClass" [value]="str('matchObjectIdPath', 'id')" [disabled]="readOnly()"
                       placeholder="data.contact.id"
                       (input)="onIdPathInput(inputValue($event))" />
                <button type="button" [disabled]="readOnly()" (click)="backToIdPathList()" title="Elegir de la lista"
                        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:text-brand-500 disabled:opacity-40 dark:border-gray-700 dark:text-gray-400">
                  <i class="fa-solid fa-list text-xs"></i>
                </button>
              </div>
            }
            <p [class]="hintClass">
              @if (samplePayloadAt()) {
                Rutas de la última llamada recibida ({{ samplePayloadAt() }}). Admite anidadas
                (<code>data.contact.id</code>) e índices (<code>items[0].id</code>).
              } @else {
                Este endpoint todavía no ha recibido ninguna llamada: se muestran las rutas más
                habituales. Envía una prueba desde el sistema de origen y aquí aparecerán las suyas.
              }
            </p>
          </div>
        </div>
      }

      @default {
        <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
          Este disparador no tiene campos configurables. Usa el modo avanzado (JSON) si necesitas ajustarlo.
        </p>
      }
    }
    }
  `
})
export class WorkflowTriggerConfigComponent extends WorkflowNodeConfigBase {
  private readonly segmentService = inject(SegmentService);
  private readonly emailTemplateApi = inject(WorkflowEmailTemplateApiService);
  private readonly webhookApi = inject(WorkflowWebhookActionApiService);
  private readonly auth = inject(AuthService);
  private readonly contactCycleApi = inject(ContactCycleApiService);
  private readonly contactStateApi = inject(ContactStateApiService);
  private readonly formsService = inject(MarketingFormsService);
  private readonly campaignApi = inject(MarketingCampaignApiService);
  private readonly valueCatalog = inject(WorkflowValueCatalogService);

  protected readonly lifecycleFields = LIFECYCLE_FIELD_OPTIONS;
  protected readonly crmObjectTypes = CRM_OBJECT_TYPE_OPTIONS;
  protected readonly sourceOptions = CONDITION_SOURCE_OPTIONS;
  protected readonly segmentEvents = SEGMENT_EVENT_OPTIONS;
  protected readonly timezones = WORKFLOW_TIMEZONE_OPTIONS;
  /** Debe coincidir con el default del backend (WorkflowDateTriggerService.DEFAULT_TIMEZONE). */
  protected readonly defaultTimezone = DEFAULT_WORKFLOW_TIMEZONE;
  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');

  /** Propiedades sugeridas del objeto base del workflow (etiqueta legible + clave). */
  protected readonly propertyOptions = computed(() => buildPropertyOptions(this.editor.workflow()?.objectType ?? null));

  /** Solo las propiedades de FECHA: es lo único que el disparador «Fecha programada» puede usar. */
  protected readonly datePropertyOptions = computed(() => buildDatePropertyOptions(this.editor.workflow()?.objectType ?? null));

  /**
   * Propiedades que el CRM publica hoy como TRANSICIÓN ({@code property}/{@code oldValue}/
   * {@code newValue}), que son las únicas con las que PROPERTY_CHANGED puede casar
   * (`WorkflowTriggerEvaluationService#evaluatePropertyChanged` compara contra `payload.property`).
   * Se muestran por su etiqueta del catálogo para no obligar a leer la clave técnica.
   */
  protected readonly propertyChangedSupported = computed<string[]>(() => {
    const keys = PROPERTY_CHANGED_SUPPORTED_KEYS[this.editor.workflow()?.objectType ?? ''] ?? [];
    const options = this.propertyOptions();
    return keys.map((key) => options.find((o) => o.key === key)?.label ?? key);
  });

  protected readonly segmentName = signal('');
  protected readonly templateName = signal('');
  protected readonly formName = signal('');
  /** INC_CRM_020 (6) inc19 — nombre del email de marketing elegido, para repintarlo al reabrir. */
  protected readonly campaignName = signal('');
  private allTemplates: EmailTemplateResponse[] = [];

  /** INBOUND_WEBHOOK: endpoints inbound existentes del negocio (para elegir la clave por nombre). */
  protected readonly endpoints = signal<WorkflowWebhookEndpointResponse[]>([]);
  /** El endpointKey guardado no está entre los endpoints cargados → conservarlo como opción extra. */
  protected readonly endpointKeyMissing = computed(() => {
    const key = this.str('endpointKey');
    return key !== '' && !this.endpoints().some((e) => e.endpointKey === key);
  });

  // ── INBOUND_WEBHOOK: ruta del id en el payload (INC_CRM_020 (9) inc54) ───────

  /**
   * Rutas propuestas cuando el endpoint todavía no ha recibido ninguna llamada. Son las que usan
   * los sistemas más habituales; en cuanto llegue una llamada real se sustituyen por las suyas.
   */
  private static readonly COMMON_ID_PATHS = ['id', 'data.id', 'contact.id', 'record.id', 'payload.id', 'object.id'];

  /** El usuario pidió escribir una ruta a mano ("Otra ruta…"). */
  private readonly manualIdPath = signal(false);

  /** Endpoint seleccionado, del que sale la muestra del payload. */
  private readonly selectedEndpoint = computed<WorkflowWebhookEndpointResponse | null>(() => {
    const key = this.str('endpointKey');
    return key ? (this.endpoints().find((e) => e.endpointKey === key) ?? null) : null;
  });

  /** Fecha de la última llamada recibida, o '' si el endpoint no ha recibido ninguna. */
  protected readonly samplePayloadAt = computed(() => {
    const at = this.selectedEndpoint()?.lastPayloadAt;
    return at ? new Date(at).toLocaleString('es-PE') : '';
  });

  protected readonly idPathOptions = computed<string[]>(() => {
    const fromSample = this.selectedEndpoint()?.payloadPaths ?? [];
    return fromSample.length ? fromSample : WorkflowTriggerConfigComponent.COMMON_ID_PATHS;
  });

  /** La ruta guardada no está entre las ofrecidas → se conserva como opción para no perderla. */
  protected readonly idPathMissing = computed(() => {
    const path = this.str('matchObjectIdPath', 'id');
    return path !== '' && !this.idPathOptions().includes(path);
  });

  /** Modo texto libre: lo pidió el usuario (la lista siempre conserva la ruta ya guardada). */
  protected readonly customIdPath = computed(() => this.manualIdPath());

  protected onIdPathSelect(value: string): void {
    if (value === '__custom__') {
      this.manualIdPath.set(true);
      return;
    }
    this.manualIdPath.set(false);
    this.patchConfig({ matchObjectIdPath: value });
  }

  protected onIdPathInput(value: string): void {
    this.manualIdPath.set(true);
    this.patchConfig({ matchObjectIdPath: value });
  }

  protected backToIdPathList(): void {
    this.manualIdPath.set(false);
  }

  constructor() {
    super();
    this.webhookApi.listEndpoints().subscribe({
      next: (list) => this.endpoints.set(list ?? []),
      error: () => {}
    });
    // Valores de la transición del evento elegido (INC_CRM_020 (9) inc51). Solo se piden cuando el
    // evento los admite; el servicio cachea cada catálogo por sesión del builder.
    effect(() => {
      const catalog = this.transitionCatalog();
      if (!catalog) {
        this.transitionValues.set([]);
        this.transitionLoaded.set(false);
        return;
      }
      this.valueCatalog.values(catalog).subscribe({
        next: (list) => {
          this.transitionValues.set(list);
          this.transitionLoaded.set(true);
        },
        error: () => {
          this.transitionValues.set([]);
          this.transitionLoaded.set(false);
        }
      });
    });
    // El catálogo de ciclos/etapas solo se pide cuando el disparador es LIFECYCLE_CHANGED, y
    // solo el del campo elegido (los otros subtipos no gastan dos GET de más).
    effect(() => {
      if (this.subtype() !== 'LIFECYCLE_CHANGED') {
        return;
      }
      // El panel PINTA "Ciclo de vida" por defecto; si el nodo no trae `field`, el backend
      // respondería MISSING_REQUIRED_CONFIG al validar/activar. Se persiste lo que se ve.
      if (this.config()['field'] == null) {
        this.patchConfig({ field: 'CYCLE' });
      }
      this.loadLifecycleCatalog(this.lifecycleField());
    });
    effect(() => {
      const id = this.num('segmentId');
      if (!id) {
        this.segmentName.set('');
        return;
      }
      this.segmentService.getSegmentById(id).subscribe({ next: (s) => this.segmentName.set(s.name), error: () => {} });
    });
    effect(() => {
      const id = this.num('emailTemplateId');
      if (!id) {
        this.templateName.set('');
        return;
      }
      const cached = this.allTemplates.find((t) => t.id === id);
      if (cached) {
        this.templateName.set(this.templateLabel(cached));
        return;
      }
      this.emailTemplateApi.list().subscribe({
        next: (list) => {
          this.allTemplates = list;
          const found = list.find((t) => t.id === id);
          if (found) {
            this.templateName.set(this.templateLabel(found));
          }
        },
        error: () => {}
      });
    });
    effect(() => {
      const id = this.num('formId');
      if (!id) { this.formName.set(''); return; }
      this.formsService.getFormById(id).subscribe({
        next: (f) => this.formName.set(f.name),
        error: () => {}
      });
    });
    // INC_CRM_020 (6) inc19 — al reabrir un nodo ya guardado, el buscador solo tiene el id: sin
    // esto el campo saldría vacío y parecería que se ha perdido la campaña elegida.
    effect(() => {
      const id = this.num('campaignId');
      if (!id) { this.campaignName.set(''); return; }
      this.campaignApi.getById(id).subscribe({
        next: (c) => this.campaignName.set(c.name),
        error: () => {}
      });
    });
  }

  // ── Disparadores de evento (CRM_EVENT / QUOTE_EVENT / MEETING_EVENT / CHATFLOW_EVENT) ──

  /** Los cuatro disparadores de evento comparten formulario: un único `config.event`. */
  protected readonly isEventTrigger = computed(() => EVENT_TRIGGER_SUBTYPES.includes(this.subtype()));

  /** Catálogo de eventos del backend (fuente única: no hay lista de claves en el frontend). */
  private readonly catalogEvents = this.editor.crmEvents;

  /** El backend respondió con el catálogo de eventos (si no, no se inventan opciones). */
  protected readonly eventCatalogLoaded = computed(() => this.catalogEvents().length > 0);

  /**
   * Claves admitidas por ESTE subtipo (`items[].eventOptions`). Si el ítem del catálogo no las
   * declara (backend anterior al campo), se ofrecen todas las del catálogo de eventos.
   */
  private readonly allowedEventKeys = computed<string[]>(() => {
    const item = this.editor.catalogItem('TRIGGER', this.subtype());
    const keys = item?.eventOptions;
    return Array.isArray(keys) && keys.length > 0 ? keys : this.catalogEvents().map((e) => e.key);
  });

  /** Eventos agrupados por `groupLabel`, conservando el orden que fija el backend. */
  protected readonly eventGroups = computed<CrmEventGroup[]>(() => {
    const allowed = new Set(this.allowedEventKeys());
    const groups: CrmEventGroup[] = [];
    for (const event of this.catalogEvents()) {
      if (!allowed.has(event.key)) {
        continue;
      }
      const label = event.groupLabel || event.group || 'Otros';
      const group = groups.find((g) => g.label === label);
      if (group) {
        group.options.push(event);
      } else {
        groups.push({ label, options: [event] });
      }
    }
    return groups;
  });

  /** Metadatos del evento elegido (etiqueta, descripción y motivo de no aplicabilidad). */
  protected readonly selectedEvent = computed<WorkflowCrmEventOptionResponse | null>(() => {
    const key = this.str('event');
    return key ? (this.catalogEvents().find((e) => e.key === key) ?? null) : null;
  });

  /**
   * El evento guardado no está entre las opciones ofrecidas → se conserva como opción extra,
   * marcada de forma evidente. Perderlo al abrir el nodo sería peor que mostrarlo inválido.
   */
  protected readonly orphanEvent = computed<OrphanEventOption | null>(() => {
    const key = this.str('event');
    if (!key || this.eventGroups().some((g) => g.options.some((o) => o.key === key))) {
      return null;
    }
    if (!this.eventCatalogLoaded()) {
      // Sin catálogo no se puede afirmar que sea inválido: se conserva tal cual, sin acusarlo.
      return { key, label: key, groupLabel: 'Valor guardado', note: '', warning: '' };
    }
    const known = this.catalogEvents().find((e) => e.key === key);
    if (known) {
      return {
        key,
        label: known.label,
        groupLabel: 'No admitido por este disparador',
        note: ' — no admitido por este disparador',
        warning: `«${known.label}» es un evento del CRM, pero este tipo de disparador no lo admite. Elige uno de la lista o usa otro disparador.`
      };
    }
    return {
      key,
      label: key,
      groupLabel: 'Ya no disponible',
      note: ' — ya no disponible',
      warning: `El evento «${key}» ya no está en el catálogo del CRM: ningún módulo lo publica, así que este workflow no se dispararía nunca. Se conserva para que veas qué tenías guardado; elige otro de la lista.`
    };
  });

  // ── Transición de un evento de cambio (INC_CRM_020 (9) inc51) ────────────────

  /**
   * Eventos cuyo payload describe una TRANSICIÓN (`oldValue` → `newValue`) y el catálogo del que
   * salen sus valores. Debe coincidir con `TRANSITION_EVENT_TYPES` del backend: un evento que no
   * esté aquí no publica los dos extremos y el filtro no casaría nunca.
   */
  private static readonly TRANSITION_CATALOGS: Record<string, { catalog: WorkflowValueCatalogKey; anyLabel: string }> = {
    CONTACT_STATE_CHANGED: { catalog: 'CONTACT_STATE', anyLabel: 'Cualquier etapa' },
    CONTACT_CYCLE_CHANGED: { catalog: 'CONTACT_CYCLE', anyLabel: 'Cualquier ciclo de vida' },
    DEAL_STAGE_CHANGED: { catalog: 'DEAL_STAGE', anyLabel: 'Cualquier etapa' },
    TICKET_STATUS_CHANGED: { catalog: 'TICKET_STATUS', anyLabel: 'Cualquier estado' },
    TICKET_PRIORITY_CHANGED: { catalog: 'TICKET_PRIORITY', anyLabel: 'Cualquier prioridad' }
  };

  private readonly transitionValues = signal<WorkflowCatalogValue[]>([]);
  private readonly transitionLoaded = signal(false);

  /**
   * Cambiar de evento cambia el CATÁLOGO de la transición: los ids de etapa, de ciclo de vida y de
   * estado de ticket no son intercambiables, así que Desde/Hacia se limpian (vacío = «cualquiera»).
   * Mismo criterio que `onLifecycleFieldChange`.
   */
  protected onEventChange(event: string): void {
    const patch: Record<string, unknown> = { event };
    const before = this.transitionCatalog();
    const key = event.trim().toUpperCase();
    const after = WorkflowTriggerConfigComponent.TRANSITION_CATALOGS[key]?.catalog ?? null;
    if (before !== after) {
      patch['fromId'] = null;
      patch['toId'] = null;
    }
    this.patchConfig(patch);
  }

  /** Catálogo de la transición del evento elegido, o null si ese evento no es de transición. */
  protected readonly transitionCatalog = computed<WorkflowValueCatalogKey | null>(() => {
    if (!this.isEventTrigger()) {
      return null;
    }
    const event = this.str('event').trim().toUpperCase();
    return WorkflowTriggerConfigComponent.TRANSITION_CATALOGS[event]?.catalog ?? null;
  });

  protected readonly anyTransitionLabel = computed(() => {
    const event = this.str('event').trim().toUpperCase();
    return WorkflowTriggerConfigComponent.TRANSITION_CATALOGS[event]?.anyLabel ?? 'Cualquiera';
  });

  protected readonly transitionOptions = computed(() =>
    this.transitionValues().map((v) => ({ id: v.id ?? v.name, label: v.label }))
  );

  /** El id guardado ya no existe en el catálogo → se conserva como opción para no perderlo. */
  protected transitionMissing(key: 'fromId' | 'toId'): boolean {
    const raw = this.str(key);
    if (raw === '' || !this.transitionLoaded()) {
      return false;
    }
    return !this.transitionOptions().some((o) => String(o.id) === raw);
  }

  // ── LIFECYCLE_CHANGED (cambio de ciclo o etapa del contacto) ─────────────────

  /** `CYCLE` (contact_cycle) o `STATE` (contact_state). El backend exige uno de los dos. */
  protected readonly lifecycleField = computed(() => (this.str('field', 'CYCLE') === 'STATE' ? 'STATE' : 'CYCLE'));

  private readonly contactCycles = signal<LifecycleOption[]>([]);
  private readonly contactStates = signal<LifecycleOption[]>([]);
  /** Catálogo ya resuelto (aunque venga vacío) → habilita la opción "ya no está en el catálogo". */
  private readonly cyclesLoaded = signal(false);
  private readonly statesLoaded = signal(false);
  protected readonly lifecycleLoadFailed = signal(false);
  /** Evita repetir el GET en cada ciclo del effect. */
  private lifecycleRequested = { CYCLE: false, STATE: false };

  /** Opciones de los desplegables Desde/Hasta según el campo elegido. */
  protected readonly lifecycleOptions = computed<LifecycleOption[]>(() =>
    this.lifecycleField() === 'STATE' ? this.contactStates() : this.contactCycles()
  );

  private readonly lifecycleCatalogLoaded = computed(() =>
    this.lifecycleField() === 'STATE' ? this.statesLoaded() : this.cyclesLoaded()
  );

  /** El id guardado ya no existe en el catálogo → se conserva como opción para no perderlo. */
  protected lifecycleMissing(key: 'fromId' | 'toId'): boolean {
    const raw = this.str(key);
    if (raw === '' || !this.lifecycleCatalogLoaded()) {
      return false;
    }
    return !this.lifecycleOptions().some((o) => String(o.id) === raw);
  }

  /**
   * Cambiar de campo cambia el CATÁLOGO: los ids de `contact_cycle` y `contact_state` no son
   * intercambiables, así que Desde/Hasta se limpian (null = «cualquiera»).
   */
  protected onLifecycleFieldChange(value: string): void {
    const field = value === 'STATE' ? 'STATE' : 'CYCLE';
    this.patchConfig({ field, fromId: null, toId: null });
    this.loadLifecycleCatalog(field);
  }

  private loadLifecycleCatalog(field: 'CYCLE' | 'STATE'): void {
    if (this.lifecycleRequested[field]) {
      return;
    }
    this.lifecycleRequested[field] = true;
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    const request$: Observable<LifecycleOption[]> = field === 'STATE'
      ? this.contactStateApi.getStates(businessId, 0, 200).pipe(map((list) => (list ?? []).map((s) => ({ id: s.id, name: s.name }))))
      : this.contactCycleApi.getCycles(businessId, 0, 200).pipe(map((list) => (list ?? []).map((c) => ({ id: c.id, name: c.name }))));
    request$.subscribe({
      next: (options) => {
        this.lifecycleLoadFailed.set(false);
        if (field === 'STATE') {
          this.contactStates.set(options);
          this.statesLoaded.set(true);
        } else {
          this.contactCycles.set(options);
          this.cyclesLoaded.set(true);
        }
      },
      error: () => {
        // Reintentable: se libera el flag para que el próximo render vuelva a pedirlo.
        this.lifecycleRequested[field] = false;
        this.lifecycleLoadFailed.set(true);
      }
    });
  }

  // ── PROPERTY_FILTER rows ─────────────────────────────────────────────────────
  /**
   * Las filas del filtro nacen con `source = OBJECT` («Propiedad del registro»), no con PAYLOAD.
   *
   * Con PAYLOAD se comparan las claves TAL CUAL las publica el módulo que emite el evento
   * (`idCompany`, `idContact`…), que no son las que ofrece el selector de propiedades
   * (`companyId`, `companyName`…): el filtro se veía bien configurado y evaluaba falso siempre.
   * Es la misma advertencia que ya llevaba `CONDITION_SOURCE_OPTIONS`. PAYLOAD sigue disponible
   * para acotar por datos propios del evento (`oldValue`, `newValue`, `changedFields`).
   */
  protected readonly filterRows = computed<FilterRow[]>(() => {
    const raw = this.arr('conditions');
    if (!raw.length) {
      return [{ source: 'OBJECT', property: '', operator: 'EQUALS', ...EMPTY_VALUE_PARTS }];
    }
    return raw.map((item) => {
      const r = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      return {
        source: String(r['source'] ?? 'OBJECT'),
        property: String(r['property'] ?? ''),
        operator: String(r['operator'] ?? 'EQUALS'),
        ...readConditionValueParts(r)
      };
    });
  });
  protected addFilter(): void {
    this.patchConfig({ conditions: [...this.filterRows(), { source: 'OBJECT', property: '', operator: 'EQUALS', ...EMPTY_VALUE_PARTS }] });
  }
  protected updateFilter(index: number, patch: Record<string, unknown>): void {
    this.patchConfig({ conditions: this.filterRows().map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  }
  protected removeFilter(index: number): void {
    this.patchConfig({ conditions: this.filterRows().filter((_, i) => i !== index) });
  }

  /** Valor de un `<select>` numérico → number, o null si es la opción vacía («cualquiera»). */
  protected numberOrNull(value: string): number | null {
    const parsed = Number(value);
    return value.trim() && Number.isFinite(parsed) ? parsed : null;
  }

  // ── selectores ───────────────────────────────────────────────────────────────

  /** Cambia el evento CRM; limpia formId cuando se sale de FORM_SUBMITTED. */
  protected onCrmEventChange(event: string): void {
    const patch: Record<string, unknown> = { event };
    if (event !== 'FORM_SUBMITTED') patch['formId'] = null;
    this.patchConfig(patch);
  }

  protected onFormSelected(f: FormListModel | null): void {
    this.formName.set(f?.name ?? '');
  }
  formSearch = (term: string, page: number): Observable<EntitySelectPage<FormListModel>> => {
    return this.formsService.getForms({ searchName: term, page, size: 10 }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages ?? 0 })),
      catchError(() => of({ items: [] as FormListModel[], totalPages: 0 }))
    );
  };
  formLabel = (f: FormListModel): string => f.name;

  // ── Interacción con email de marketing (INC_CRM_020 (6) inc19) ──────────────────

  /**
   * Las siete interacciones, tomadas del catálogo del backend igual que el resto de eventos.
   *
   * El panel anterior las tenía escritas a mano y en inglés (`['OPENED','CLICKED','BOUNCED',
   * 'UNSUBSCRIBED']`), con dos problemas: se veían en inglés en una UI en español y la lista había
   * derivado de lo que el backend publica de verdad — `UNSUBSCRIBED` no existe como evento (la
   * baja llega como queja de spam), así que elegirlo producía un workflow que no arrancaba nunca.
   */
  protected readonly emailInteractionOptions = computed<WorkflowCrmEventOptionResponse[]>(() =>
    this.catalogEvents().filter((e) => e.key.startsWith('MARKETING_EMAIL_'))
  );

  protected readonly selectedEmailInteraction = computed<WorkflowCrmEventOptionResponse | null>(() => {
    const key = this.str('event');
    return key ? (this.emailInteractionOptions().find((e) => e.key === key) ?? null) : null;
  });

  /** Al volver a «cualquier email» se limpia la campaña: dejarla puesta acotaría en silencio. */
  protected onEmailScopeChange(scope: string): void {
    const patch: Record<string, unknown> = { scope };
    if (scope !== 'CAMPAIGN') {
      patch['campaignId'] = null;
      this.campaignName.set('');
    }
    this.patchConfig(patch);
  }

  protected onCampaignSelected(c: MarketingCampaign | null): void {
    this.campaignName.set(c?.name ?? '');
  }

  /**
   * `GET /api/marketing/campaigns` devuelve la lista completa sin paginar ni buscar, así que el
   * filtrado por texto se hace aquí. Es asumible: el listado de campañas de un negocio es corto y
   * ya se consume igual en el módulo de Marketing.
   */
  campaignSearch = (term: string): Observable<EntitySelectPage<MarketingCampaign>> => {
    return this.campaignApi.list().pipe(
      map((list) => {
        const t = term.trim().toLowerCase();
        const items = t
          ? list.filter((c) => `${c.name ?? ''} ${c.subject ?? ''}`.toLowerCase().includes(t))
          : list;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as MarketingCampaign[], totalPages: 0 }))
    );
  };
  campaignLabel = (c: MarketingCampaign): string => c.name;
  campaignSublabel = (c: MarketingCampaign): string => c.subject ?? '';

  protected onSegmentSelected(s: Segment | null): void {
    this.segmentName.set(s?.name ?? '');
  }
  segmentSearch = (term: string, page: number): Observable<EntitySelectPage<Segment>> => {
    return this.segmentService.getSegments(page, 10, { searchTerm: term }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as Segment[], totalPages: 0 }))
    );
  };
  segmentLabel = (s: Segment): string => s.name;

  protected onTemplateSelected(t: EmailTemplateResponse | null): void {
    this.templateName.set(t ? this.templateLabel(t) : '');
  }
  templateSearch = (term: string): Observable<EntitySelectPage<EmailTemplateResponse>> => {
    return this.emailTemplateApi.list().pipe(
      map((list) => {
        this.allTemplates = list;
        const t = term.trim().toLowerCase();
        const items = t ? list.filter((x) => `${x.subject ?? ''} ${x.purpose ?? ''}`.toLowerCase().includes(t)) : list;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as EmailTemplateResponse[], totalPages: 0 }))
    );
  };
  templateLabel = (t: EmailTemplateResponse): string => t.subject || t.purpose;
  templateSublabel = (t: EmailTemplateResponse): string => `${t.purpose}${t.locale ? ' · ' + t.locale : ''} v${t.version}`;
}
